import Survicate from '@survicate/survicate-web-package/survicate_widget';

type SurvicateWindow = Window & { _svc?: { initialized?: boolean } };
type ReadySignal = 'init' | 'event' | 'svc';

export type SurvicateTracker = (name: string, props: Record<string, string | number>) => void;

const READY_EVENT = 'SurvicateReady';
const READY_WARN_AFTER_MS = 15000;

// Resolves once web_surveys.json is loaded and the SDK is ready. The SDK builds
// its event→survey map on the first invokeEvent of a page session and never
// rebuilds it, so any call made before this resolves can silently disable every
// event-triggered survey until reload. If the survey fetch fails, this never
// resolves — so never await this in a control-flow path; only chain SDK calls
// onto it via whenSurvicateReady.
let ready: Promise<void> | null = null;

let track: SurvicateTracker = () => undefined;

// Never let telemetry break the SDK path.
function safeTrack(name: string, props: Record<string, string | number>): void {
    try {
        track(name, props);
    } catch (error) {
        console.error(error);
    }
}

// The single workspace-key check. With no key, `ready` stays null and every
// whenSurvicateReady call is a no-op.
export function initSurvicate(workspaceKey: string | undefined, tracker?: SurvicateTracker): void {
    if (ready || !workspaceKey) {
        return;
    }
    if (tracker) {
        track = tracker;
    }
    const startedAt = performance.now();
    const sdkInit = Survicate.init({ workspaceKey });
    let resolved = false;
    // Resolve on whichever readiness signal comes first. The SDK attaches its own
    // SurvicateReady listener late (after rendering the widget), so its init()
    // promise can miss the event and hang even on a successful load. Our listener
    // is attached synchronously here, before any async SDK work.
    ready = new Promise<void>((resolve) => {
        const done = (signal: ReadySignal) => {
            if (resolved) {
                return;
            }
            resolved = true;
            window.removeEventListener(READY_EVENT, onEvent);
            safeTrack('survicate_ready', { signal, ms: Math.round(performance.now() - startedAt) });
            resolve();
        };
        const onEvent = () => done('event');
        window.addEventListener(READY_EVENT, onEvent);
        // init() swallows its own errors, so this never rejects in practice.
        Promise.resolve(sdkInit).then(
            () => done('init'),
            () => undefined
        );
        if ((window as SurvicateWindow)._svc?.initialized) {
            done('svc');
        }
    });
    // A hung gate is otherwise silent. Runs in all environments; a late resolve
    // still tracks survicate_ready with the real ms, so late-but-OK loads can be
    // told apart from hangs.
    window.setTimeout(() => {
        if (!resolved) {
            safeTrack('survicate_not_ready', { ms: READY_WARN_AFTER_MS });
            if (process.env.NODE_ENV !== 'production') {
                console.warn('Survicate not ready after 15s; queued SDK calls are waiting.');
            }
        }
    }, READY_WARN_AFTER_MS);
}

// Runs `fn` after the SDK is ready. No-op when Survicate was never initialized
// (no workspace key), so callers need no key guard of their own.
export function whenSurvicateReady(fn: () => void): void {
    if (!ready) {
        return;
    }
    ready.then(fn).catch((error) => console.error(error));
}

// Telemetry for each app → SDK event, called right before Survicate.invokeEvent.
export function trackSurvicateEvent(eventName: string): void {
    safeTrack('survicate_event_invoked', { event: eventName });
}
